/**
 * PortfolioChart.js — every deal at once: MOIC against what it took
 * =================================================================
 * Usability review of 23 October 2026, item 26: *"A small bubble or bar chart
 * of MOIC versus amount invested per deal, colored by strategy, would give the
 * 30-second view a PM wants before scrolling into the list."*
 *
 * One dot per deal in the list below it, so the filter and the search above
 * narrow both. Three rules keep it honest, and they are the deal list's rules:
 *
 *   - Invested is in dollars or the deal is not drawn. A deal that reads in its
 *     own currency is plotted at the dollars the server converted its capital
 *     to on each date (`summary_usd.capital_deployed`); where there is no such
 *     figure the deal is left out and counted, never plotted at face value —
 *     a krona sum on a dollar axis is the defect of D-136 drawn as a picture.
 *   - A deal with no MOIC — withheld for a missing rate, or a floor because its
 *     investment accounts are below zero — is left out and counted (CLAUDE.md
 *     0.30, 0.32: a ratio that is not a figure is not ranked or drawn).
 *   - Both axes are logarithmic, because the book runs from USD 14,000 to
 *     USD 10 million and from 0.6x to 20x, and a linear axis would put most of
 *     it in one corner. A log axis has no zero, so a deal that returned nothing
 *     cannot be drawn; it is named under the chart rather than dropped.
 *
 * Colour follows the strategy, never its rank on screen: slots go to the seven
 * strategies with the most deals on the whole book, so a filter that hides
 * some deals repaints none of the others; the rest share a neutral "Other".
 * A scatter compares every colour with every other, which no seven colours
 * survive for every reader, so each slot also has its own marker shape.
 */
import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Symbols,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "../../components/ui";
import { InfoTip } from "../../components/PageTools";
import { compactMoney, multiple, toNumber } from "../../components/format";
import theme from "../../components/theme";
import { dealMoney } from "./figures";
import { sortValue } from "./stats";
import { strategyLabel } from "./listing";

/* The Dashboard's four (#3a6fb0, #c46a28, #1f9470, #9a5bb5), extended to
   seven and validated together with the dataviz skill's validate_palette.js
   (--mode light --surface #ffffff): every check passes on adjacent pairs,
   worst CVD ΔE 9.4. Across all pairs no seven colours pass, which is why each
   slot also carries a shape. */
export const STRATEGY_COLOURS = ["#3a6fb0", "#c46a28", "#1f9470", "#9a5bb5", "#b8860b", "#b0458f", "#7a8a1e"];
const SHAPES = ["circle", "square", "triangle", "diamond", "star", "wye", "cross"];
const OTHER = { colour: "#9b938b", shape: "circle", label: "Other or untagged" };

/** Which strategies get a colour: the most deals on the whole book first, ties by name. */
export function strategySlots(allDeals) {
  const counts = new Map();
  (allDeals || []).forEach((d) => {
    const tag = d.deal?.strategy;
    if (tag) counts.set(tag, (counts.get(tag) || 0) + 1);
  });
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "en", { sensitivity: "base" }));
  return Object.fromEntries(
    ranked.slice(0, STRATEGY_COLOURS.length).map(([tag], i) => [tag, { colour: STRATEGY_COLOURS[i], shape: SHAPES[i], label: tag }])
  );
}

/** A deal's invested figure in dollars, or null when there is none to plot. */
export function investedUsd(deal) {
  const stats = deal.stats;
  if (!stats || stats.measured === false) return null;
  if ((stats.currency || "USD") === "USD") return toNumber(stats.capital_deployed);
  const usd = deal.summary_usd;
  if (usd && (usd.currency || "USD") === "USD") return toNumber(usd.capital_deployed);
  return null;
}

/**
 * The dots, and what was left out and why. Every deal in `deals` lands in
 * exactly one of the four.
 */
export function portfolioPoints(deals, slots) {
  const points = [];
  const noMoic = [];
  const noUsd = [];
  const notDrawable = [];
  (deals || []).forEach((d) => {
    const moic = sortValue(d.stats, "moic");
    if (moic === null) {
      noMoic.push(d);
      return;
    }
    const invested = investedUsd(d);
    if (invested === null) {
      noUsd.push(d);
      return;
    }
    if (!(invested > 0) || !(moic > 0)) {
      notDrawable.push(d);
      return;
    }
    const slot = slots[d.deal.strategy] || OTHER;
    points.push({
      id: d.deal.id,
      name: d.deal.name,
      strategy: strategyLabel(d.deal.strategy),
      x: invested,
      y: moic,
      colour: slot.colour,
      shape: slot.shape,
      group: slots[d.deal.strategy] ? d.deal.strategy : "__other__",
      converted: (d.stats.currency || "USD") !== "USD" || Boolean(d.stats.converted),
    });
  });
  return { points, noMoic, noUsd, notDrawable };
}

// What was left out, in one line.
export function leftOutText({ noMoic, noUsd, notDrawable }) {
  const bits = [];
  if (noMoic.length) {
    bits.push(`${noMoic.length} with no MOIC (no capital went in, as on an equity line; an exchange rate is missing; or the investment accounts are below zero)`);
  }
  if (noUsd.length) bits.push(`${noUsd.length} with no dollar figure for what it invested`);
  if (notDrawable.length) {
    bits.push(
      `${notDrawable.map((d) => `${d.deal.name} (${multiple(sortValue(d.stats, "moic"))})`).join(", ")} — a log scale has no zero`
    );
  }
  return bits.length ? `Not drawn: ${bits.join("; ")}.` : "";
}

const ladder = (lo, hi, steps) => steps.filter((v) => v >= lo && v <= hi);
const X_STEPS = [1e3, 3e3, 1e4, 3e4, 1e5, 3e5, 1e6, 3e6, 1e7, 3e7, 1e8];
const Y_STEPS = [0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100];

export function axisDomains(points) {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  // The nearest steps of the ladder either side of the data: a domain that
  // runs a decade past the largest deal leaves a fifth of the chart empty.
  const xLo = X_STEPS.filter((v) => v <= Math.min(...xs)).pop() || X_STEPS[0];
  const xHi = X_STEPS.find((v) => v >= Math.max(...xs)) || X_STEPS[X_STEPS.length - 1];
  const yLo = Y_STEPS.filter((v) => v <= Math.min(0.5, ...ys)).pop() || Y_STEPS[0];
  const yHi = Y_STEPS.find((v) => v >= Math.max(2, ...ys)) || Y_STEPS[Y_STEPS.length - 1];
  return { x: [xLo, xHi], y: [yLo, yHi], xTicks: ladder(xLo, xHi, X_STEPS), yTicks: ladder(yLo, yHi, Y_STEPS) };
}

function Dot({ cx, cy, payload, focus, onOpen }) {
  if (cx === undefined || cy === undefined) return null;
  const faded = focus && focus !== payload.group;
  return (
    <g
      style={{ cursor: "pointer" }}
      opacity={faded ? 0.18 : 1}
      onClick={() => onOpen(payload.id)}
      role="link"
      aria-label={`${payload.name}: ${multiple(payload.y)} on ${dealMoney(payload.x)} invested`}
    >
      {/* A hit target bigger than the mark. */}
      <circle cx={cx} cy={cy} r={11} fill="transparent" />
      <Symbols cx={cx} cy={cy} type={payload.shape} size={88} fill={payload.colour} stroke="#ffffff" strokeWidth={1.5} />
    </g>
  );
}

function PointTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="dr-chart-tip">
      <div style={{ fontWeight: 600, color: theme.text }}>{p.name}</div>
      <div style={{ color: theme.textMuted, marginBottom: 4 }}>{p.strategy}</div>
      <div>MOIC {multiple(p.y)}</div>
      <div>
        Invested {dealMoney(p.x)}
        {p.converted ? " (converted at each date's fixing)" : ""}
      </div>
      <div style={{ color: theme.textMuted, marginTop: 4 }}>Click to open the deal</div>
    </div>
  );
}

export function PortfolioChart({ deals, allDeals, onOpenDeal, scopeText }) {
  const slots = useMemo(() => strategySlots(allDeals), [allDeals]);
  const result = useMemo(() => portfolioPoints(deals, slots), [deals, slots]);
  const [focus, setFocus] = useState(null);
  const { points } = result;

  const legend = useMemo(() => {
    const present = new Map();
    points.forEach((p) => present.set(p.group, (present.get(p.group) || 0) + 1));
    const named = Object.entries(slots)
      .filter(([tag]) => present.has(tag))
      .map(([tag, s]) => ({ key: tag, ...s, count: present.get(tag) }));
    if (present.has("__other__")) named.push({ key: "__other__", ...OTHER, count: present.get("__other__") });
    return named;
  }, [points, slots]);

  const note = leftOutText(result);

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          MOIC against amount invested
          <InfoTip wide>
            <p>
              One mark per deal in the list below: across, what it invested in dollars; up, its
              MOIC — (cash back + what is still held) ÷ invested. Above the 1.0x line a deal has more
              than its money back, counting what it still holds at cost or at its mark.
            </p>
            <p>
              Both axes are logarithmic: each step is a multiple, so a deal of USD 50,000 and one of
              USD 5 million both have room. A deal that reads in another currency is plotted at the
              dollars each outflow converted to on its own date; one with no dollar figure, or no
              MOIC, is not drawn and is counted under the chart.
            </p>
          </InfoTip>
        </span>
      }
      subtitle={`${points.length} ${points.length === 1 ? "deal" : "deals"} — ${scopeText}. Click a mark to open the deal.`}
    >
      {legend.length > 0 && (
        <div className="dr-legend" role="group" aria-label="Strategies">
          {legend.map((item) => {
            const on = focus === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`dr-legend-item${on ? " is-on" : ""}${focus && !on ? " is-off" : ""}`}
                aria-pressed={on}
                title={on ? "Show every strategy" : `Pick out ${item.label}`}
                onClick={() => setFocus(on ? null : item.key)}
              >
                <svg width="14" height="14" aria-hidden="true">
                  <Symbols cx={7} cy={7} type={item.shape} size={64} fill={item.colour} />
                </svg>
                {item.label}
                <span className="dr-legend-count">{item.count}</span>
              </button>
            );
          })}
        </div>
      )}
      {points.length ? (
        <ChartBody points={points} focus={focus} onOpenDeal={onOpenDeal} />
      ) : (
        <div style={{ fontSize: 13, color: theme.textMuted, padding: "24px 0" }}>
          No deal in the list has a MOIC and a dollar figure to draw.
        </div>
      )}
      {note && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 6 }}>{note}</div>}
    </Card>
  );
}

function ChartBody({ points, focus, onOpenDeal }) {
  const { x, y, xTicks, yTicks } = useMemo(() => axisDomains(points), [points]);
  return (
    <div style={{ width: "100%", height: 300 }}>
      <ResponsiveContainer>
        <ScatterChart margin={{ top: 10, right: 18, bottom: 18, left: 4 }}>
          <CartesianGrid stroke={theme.borderLight} strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="x"
            name="Invested"
            scale="log"
            domain={x}
            ticks={xTicks}
            tickFormatter={(v) => compactMoney(v)}
            tick={{ fontSize: 11, fill: theme.textMuted }}
            stroke={theme.border}
            label={{ value: "Invested (USD, log scale)", position: "insideBottom", offset: -12, fontSize: 11, fill: theme.textMuted }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="MOIC"
            scale="log"
            domain={y}
            ticks={yTicks}
            tickFormatter={(v) => `${v}x`}
            tick={{ fontSize: 11, fill: theme.textMuted }}
            stroke={theme.border}
            width={44}
          />
          <ReferenceLine
            y={1}
            stroke={theme.textMuted}
            strokeDasharray="4 3"
            label={{ value: "1.0x", position: "insideTopRight", fontSize: 11, fill: theme.textMuted }}
          />
          <ChartTooltip content={<PointTooltip />} cursor={false} />
          <Scatter
            data={points}
            isAnimationActive={false}
            shape={(props) => <Dot {...props} focus={focus} onOpen={onOpenDeal} />}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
